"use client";

import { useSubscription } from "@/features/subscription/queries";
import { SubscriptionPlanId } from "@/features/subscription/types";
import type { CreditCapabilities, UserSubscription } from "@/features/subscription/types";

export { formatCredits } from "@speakai/common/src/subscription/format-credits";

export const LOW_CREDIT_THRESHOLD = 0.5;

const numOrZero = (n: number | undefined | null): number =>
  typeof n === "number" && Number.isFinite(n) && n > 0 ? n : 0;

const floorVal = (n: number): number => (Number.isFinite(n) && n > 0 ? Math.floor(n) : 0);

export const compactChars = (n: number): string => {
  if (!Number.isFinite(n) || n <= 0) {
    return new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 0 }).format(0);
  }
  const step = Math.pow(10, Math.floor(Math.log10(n)));
  const ceiled = Math.ceil(n / step) * step;
  return new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(ceiled);
};

// Accurate compact format with no round-up — for values that are exact or
// derived multiples (e.g. chat characters, annual = monthly x 12) where the
// generous round-up of compactChars would overstate the real figure
// (12,000,000 -> "12M", not "20M").
export const formatCompact = (n: number): string => {
  if (!Number.isFinite(n) || n <= 0) {
    return new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 0 }).format(0);
  }
  return new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(n);
};


/** One credit-capability line: resolved English text plus the i18n key/params to translate it. */
export interface CreditCapabilityLine {
  key: string;
  text: string;
  params: Record<string, unknown>;
}

/**
 * What one month of credits buys, one service per line, floored so it never
 * overstates. Each line is short enough to render as a single checkmark row.
 * Model names are deliberately not disclosed (same policy as the chat UI).
 */
export function creditCapabilityLines(cv: CreditCapabilities | undefined): CreditCapabilityLine[] {
  if (!cv) return [];
  const lines: CreditCapabilityLine[] = [];
  if (cv.transcriptionHours > 0) {
    const hours = floorVal(cv.transcriptionHours);
    lines.push({
      key: "PRICING.CREDIT_CAP.TRANSCRIPTION",
      text: `${hours} hrs of transcription`,
      params: { hours },
    });
  }
  if (cv.meetingHours > 0) {
    const hours = floorVal(cv.meetingHours);
    lines.push({
      key: "PRICING.CREDIT_CAP.MEETINGS",
      text: `${hours} hrs of AI meetings`,
      params: { hours },
    });
  }
  if (cv.translationCharacters > 0) {
    const chars = compactChars(cv.translationCharacters);
    lines.push({
      key: "PRICING.CREDIT_CAP.TRANSLATION",
      text: `${chars} translation characters`,
      params: { chars },
    });
  }
  if (cv.aiChats > 0) {
    const count = floorVal(cv.aiChats).toLocaleString();
    lines.push({
      key: "PRICING.CREDIT_CAP.CHATS",
      text: `${count} AI chats`,
      params: { count },
    });
  }
  return lines;
}

/**
 * The capability lines joined with "or" — they are alternatives, not additive.
 * Pass the component's `t` to localise each line; omitted, the English text is used.
 */
export function creditCapabilitySummary(
  cv: CreditCapabilities | undefined,
  translate?: (key: string, options: { defaultValue: string } & Record<string, unknown>) => string,
): string {
  return creditCapabilityLines(cv)
    .map((line) =>
      translate ? translate(line.key, { defaultValue: line.text, ...line.params }) : line.text,
    )
    .join(" or ");
}

export interface DerivedCredits {
  creditMode: boolean;
  /** Pay-As-You-Go has no grant and runs purely on the wallet, so grantRemaining is always 0.
   * Balance UIs must show `remaining` (the wallet) for PAYG, not `grantRemaining`. */
  isPayg: boolean;
  granted: number;
  used: number;
  /** Grant left this cycle (subscription credits), wallet excluded. */
  grantRemaining: number;
  /** Total spendable = grantRemaining + wallet (from the balance endpoint). Affordability only. */
  remaining: number;
  isLow: boolean;
  percentUsed: number;
  byType: Record<string, number>;
}

export interface CreditsState extends DerivedCredits {
  isLoading: boolean;
}

export function deriveCredits(subscription: UserSubscription | undefined): DerivedCredits {
  const creditMode = subscription?.creditMode === true;
  const granted = numOrZero(subscription?.creditsGranted);
  const used = numOrZero(subscription?.creditsUsed);
  const grantRemaining = numOrZero(subscription?.creditsGrantRemaining);
  const remaining = numOrZero(subscription?.creditsRemaining);
  const byType = Object.fromEntries(
    Object.entries(subscription?.creditsByType ?? {}).map(([k, v]) => [k, numOrZero(v as number)]),
  );

  return {
    creditMode,
    isPayg: subscription?.planId === SubscriptionPlanId.Speak_Pay_As_You_Go,
    granted,
    used,
    grantRemaining,
    remaining,
    isLow: creditMode && remaining <= LOW_CREDIT_THRESHOLD,
    percentUsed: granted ? Math.min((used / granted) * 100, 100) : 0,
    byType,
  };
}

export function useCredits({ enabled = true }: { enabled?: boolean } = {}): CreditsState {
  const { data: subscription, isLoading } = useSubscription({ enabled });

  return {
    ...deriveCredits(subscription),
    isLoading,
  };
}
